import styles from "./page.module.css";
import Switch from "@/components/forms/Switch/Switch";
import Select from "@/components/forms/Select/Select";
import { useUserContext } from "@/contexts/UserContext";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import { useEffect, useState } from "react";
import { AutoFillListPriceFrom, AutoFillListPriceOperation, BoxContentsProvider, FulfillmentType, ShippingTemplate, TransportType } from "@boxem/core-api-schema";
import Button from "@/components/forms/Button/Button";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import { createSettings, updateUserSettings } from "@/api/settings";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import ChangeSubscriptionModal, { ChangeSubscriptionModalMode } from "@/components/ChangeSubscriptionModal/ChangeSubscriptionModal";
import SellerAccountBanner from "@/components/SellerAccountBanner/SellerAccountBanner";
import Flex from "@/components/Flex/Flex";
import useFeatureFlag from "@/hooks/useFeatureflag";
import PageContainer from "../container/PageContainer";
import AddressDropdown from "@/components/AddressDropdown/AddressDropdown";

export default function SettingsDetailsPage() {
  const { user, setUser, addressBook, seller, hasTriedToGetUserFromLocalStorage } = useUserContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { subscription } = useSubscriptionContext();
  const [shippingMethodStates, setShippingMethodStates] = useState([true, (subscription && subscription.planCode.indexOf('professional') >= 0)]);
  const [boxContentStates, setBoxCententStates] = useState([true, true, (subscription && subscription.planCode.indexOf('professional') >= 0)]);
  const [newPlanCode] = useState('professional-monthly-inapp');
  const [changeSubscriptionModalMode, setChangeSubscriptionModalMode] = useState(ChangeSubscriptionModalMode.closed);
  const [loading, setLoading] = useState<boolean>(false);
  const [templateOptions, setTemplateOptions] = useState<ShippingTemplate[]>([]);
  const launchSideBar = useFeatureFlag("newSideBar");
  const [autoFillPrice, setAutoFillPrice] = useState<number>(parseFloat(user.settings?.autoFillListPriceAmount));
  
  useEffect(() => {
    if (hasTriedToGetUserFromLocalStorage && !!user && !user.settings?.id) {
      createSettings().then(
        settings => {
          setUser({...user, settings: settings }, false);
        }
      );
    }
  }, [user, hasTriedToGetUserFromLocalStorage]);

  useEffect(() => {
    setShippingMethodStates([true, (subscription && subscription.planCode.indexOf('professional') >= 0)]);
    setBoxCententStates([true, true, (subscription && subscription.planCode.indexOf('professional') >= 0)]);
  }, [subscription]);

  const updateSettings = () => {
    setUser({...user}, false); // update form UI for choice made immediately
    setLoading(true);

    // if either of these are false, they should force another bool false too.
    if (!user.settings.printFNSKUAfterAddingToShipment) {
      user.settings.printFNSKUAfterIncreaseQuantityInShipment = false;
    }
    if (!user.settings.printFNSKUAfterAssigningToBox) {
      user.settings.printFNSKUAfterIncreaseQuantityInBox = false;
    }

    updateUserSettings(user.settings)
      .then(settings => setUser({...user, settings }, false))
      .catch(errors => {
        if (handleErrorResponseGenerically(errors)) {
          return;
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setTemplateOptions(seller?.shippingTemplates);
  }, [seller]);

  if (!user || !user.settings?.id) {
    return <LoadingSpinner />
  }

  return (
    <PageContainer>
      <ChangeSubscriptionModal
        targetPlanCode={newPlanCode}
        mode={changeSubscriptionModalMode}
        setMode={setChangeSubscriptionModalMode}
        onClose={() => {
          setChangeSubscriptionModalMode(ChangeSubscriptionModalMode.closed);
        }}
      />

      <Flex direction="column" gap={4}>
        <SellerAccountBanner/>
        
        <Flex justify="space-between" gap={4}>
          <Flex direction="column" gap={2} className={launchSideBar ? styles.formDescriptionNew : styles.formDescription}>
            <h3>Fulfillment</h3>
            <p>Set your fulfillment preferences, focusing on the method of product handling and delivery.</p>
          </Flex>

          <Flex direction="column" gap={2} className={styles.formItem}>
            <Flex direction="column" gap={2}>
              <label htmlFor="fulfillmentType" className="strong">Fulfillment Type</label>
              <Select
                id="fulfillmentType"
                placeholder="l"
                fullWidth
                options={[FulfillmentType.FBA, FulfillmentType.FBM]}
                defaultOption={user.settings.fulfillmentType ?? FulfillmentType.FBA}
                optionRender={option => {
                  if (option === FulfillmentType.FBA) {
                    return "FBA (Fulfillment By Amazon)"
                  } else if (option === FulfillmentType.FBM) {
                    return "FBM (Fulfillment By Merchant)"
                  }
                }}
                onChange={(e: FulfillmentType) => {
                  user.settings.fulfillmentType = e;
                  updateSettings();
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <div className={styles.divider}></div>
        <Flex justify="space-between" gap={4}>
          <Flex direction="column" gap={2} className={styles.formDescription}>
            <h3>FBM Shipping Template</h3>
            <p>
              Set the default Shipping Template for your FBM Listings. You can only create Shipping Templates within Seller Central.
              Checkout this help article for more details:
              <a href="https://boxem.com/blog/how-to-create-shipping-templates-for-fulfillment-by-merchant-fbm" target="_blank">Creating Shipping Templates</a>
            </p>
          </Flex>

          <Flex direction="column" gap={2} className={styles.formItem}>
            <Flex direction="column" gap={2}>
              <label htmlFor="shippingTemplate" className="strong">Shipping Template</label>
              <Select
                id="shippingTemplate"
                placeholder="None"
                disabled={templateOptions?.length === 0}
                fullWidth
                options={templateOptions}
                defaultOption={templateOptions?.find(option => option.id === seller.settings.fbmShippingTemplateId)}
                value={templateOptions?.find(option => option.id === user.settings.fbmShippingTemplateId)}
                optionRender={option => option.name}
                onChange={(e: ShippingTemplate) => {
                  user.settings.fbmShippingTemplateId = e.id;
                  updateSettings();
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <div className={styles.divider}></div>
        <Flex justify="space-between" gap={4}>
          <Flex direction="column" gap={2} className={styles.formDescription}>
            <h3>Shipping & Origin</h3>
            <p>Choose the starting point and method for your shipments, including a shipping address and the preferred delivery method.</p>
          </Flex>

          <Flex direction="column" gap={4} className={styles.formItem}>
            <Flex direction="column" gap={2}>
              <label htmlFor="shipFrom" className="strong">Ship from</label>
              <AddressDropdown
                addressBook={addressBook}
                disabled={loading}
                value={user.settings?.fromId}
                onChange={id => {
                  user.settings.fromId = id;
                  updateSettings();
                }}
              />
            </Flex>

            <Flex direction="column" gap={2}>
              <label htmlFor="shippingMethod" className="strong">Shipping method</label>
              <Select
                id="shippingMethod"
                disabled={loading}
                options={[TransportType.SPD, TransportType.LTL]}
                optionStates={shippingMethodStates}
                optionRender={(option) => {
                  switch (option) {
                    case TransportType.SPD:
                      return "SPD (Small Parcel Delivery)";

                    case TransportType.LTL:
                      return (<Flex justify="space-between" align="center">LTL (Less than Truck Load){(subscription && subscription.planCode.indexOf('professional') < 0) ? <Button type="primary" onClick={(e) => {
                        e.stopPropagation(true);
                        // open the upgrade plan page modal
                        setChangeSubscriptionModalMode(ChangeSubscriptionModalMode.update);
                      }} className={styles.upgradeButton}>Upgrade Plan</Button> : <></>}</Flex>);
                  }
                }}
                selectionRender={option => {
                  switch (option) {
                    case TransportType.SPD:
                      return "SPD (Small Parcel Delivery)";

                    case TransportType.LTL:
                      return "LTL (Less than Truck Load)";
                  }
                }}
                placeholder="SPD (Small Parcel Delivery)"
                fullWidth
                value={user.settings?.shippingMethod}
                onChange={e => {
                  user.settings.shippingMethod = e;
                  user.settings.boxContentsPreference = BoxContentsProvider.Barcode2D;
                  updateSettings();
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <div className={styles.divider}></div>
        <Flex justify="space-between" gap={4}>
          <Flex direction="column" gap={2} className={styles.formDescription}>
            <h3>Packaging & Labeling, 2D Barcodes</h3>
            <p>Customize how the contents of your boxes are transmitted to Amazon, and define rules for printing labels.</p>
          </Flex>

          <Flex direction="column" gap={4} className={styles.formItem}>
            <Flex direction="column" gap={2}>
              <label htmlFor="boxContentsPreference" className="strong">Box contents preference</label>
              <Select
                id="boxContentsPreference"
                options={[BoxContentsProvider.Boxem,BoxContentsProvider.Barcode2D]}
                optionStates={boxContentStates}
                disabled={user.settings?.shippingMethod === TransportType.LTL || loading}
                optionRender={option => {
                  switch (option) {
                    case BoxContentsProvider.Boxem:
                      return "Boxem Provided";

                    case BoxContentsProvider.Seller:
                      const month = new Date().getMonth();
                      const isNovemberOrDecember = month === 10 || month === 11;
                      return `No box contents (${isNovemberOrDecember ? "$0.30" : "$0.15"} per unit fee)`;

                    case BoxContentsProvider.Barcode2D:
                      return (<Flex justify="space-between" align="center">2D Barcodes{(subscription && subscription.planCode.indexOf('professional') < 0) ? <Button type="primary" onClick={(e) => {
                        e.stopPropagation(true);
                        // open the upgrade plan page modal
                        setChangeSubscriptionModalMode(ChangeSubscriptionModalMode.update);
                      }} className={styles.upgradeButton}>Upgrade Plan</Button> : <></>}</Flex>);
                  }
                }}
                selectionRender={option => {
                  switch (option) {
                    case BoxContentsProvider.Boxem:
                      return "Boxem Provided";

                    case BoxContentsProvider.Seller:
                      const month = new Date().getMonth();
                      const isNovemberOrDecember = month === 10 || month === 11;
                      return `No box contents (${isNovemberOrDecember ? "$0.30" : "$0.15"} per unit fee)`;

                    case BoxContentsProvider.Barcode2D:
                      return "2D Barcodes";
                  }
                }}
                fullWidth
                value={user.settings?.boxContentsPreference}
                onChange={e => {
                  user.settings.boxContentsPreference = e;
                  updateSettings();
                }}
              />
            </Flex>

            <Flex direction="column" gap={2} className={styles.formDescription}>
              <h3>Auto-Print FNSKU</h3>
              <p>Automatically print FNSKU labels as you add products to a batch and/or as you add products to boxes.</p>
            </Flex>
            <Flex align="center" className={styles.formDescription}>
              <Flex direction="column" gap={2}>
                <Switch
                  disabled={loading}
                  label="After assigning to boxes"
                  caption="Labels will print after assigning products to boxes"
                  value={user.settings.printFNSKUAfterAssigningToBox}
                  onChange={v => {
                    user.settings.printFNSKUAfterAddingToShipment = false; // unset this, they are mutually exclusive
                    user.settings.printFNSKUAfterAssigningToBox = v;
                    updateSettings();
                  }}
                />
                <Tooltip
                  title={user.settings.printFNSKUAfterAddingToShipment ? "Switch on 'After assigning to boxes' first" : ""}
                  placement="top"
                >
                  <Switch
                    disabled={loading || user.settings.printFNSKUAfterAddingToShipment}
                    label="On quantity update"
                    caption="When increasing the quantity of a product within a box, print additional labels"
                    value={user.settings.printFNSKUAfterIncreaseQuantityInBox}
                    onChange={v => {
                      user.settings.printFNSKUAfterIncreaseQuantityInBox = v;
                      updateSettings();
                    }}
                  />
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        <div className={styles.divider}></div>
        <Flex justify="space-between" gap={4}>
          <Flex direction="column" gap={2} className={styles.formDescription}>
            <h3>Pricing</h3>
            <p>Automatically set the list price based on the buy box or highest/lowest FBA offer.</p>
          </Flex>

          <Flex direction="column" gap={4} className={styles.formItem}>
            <Switch
              disabled={loading}
              label="Auto-fill list price"
              caption="Enable to configure the list price default."
              value={user.settings.autoFillListPrice}
              onChange={v => {
                user.settings.autoFillListPrice = v;
                updateSettings();
              }}
            />
            <Flex gap={2} align="center">
              <span>Match list price with</span>
              <span>
                <Tooltip title={!user.settings.autoFillListPrice ? 'Enable Auto-fill list price first' : ''}>
                  <Select
                    disabled={!user.settings.autoFillListPrice || loading}
                    value={user.settings?.autoFillListPriceFrom}
                    onChange={e => {
                      user.settings.autoFillListPriceFrom = e;
                    }}
                    options={[AutoFillListPriceFrom.BUY_BOX,AutoFillListPriceFrom.HIGHEST_FBA_OFFER,AutoFillListPriceFrom.LOWEST_FBA_OFFER]}
                    optionRender={option => {
                      switch (option) {
                        case AutoFillListPriceFrom.BUY_BOX:
                          return "Buy Box";

                        case AutoFillListPriceFrom.HIGHEST_FBA_OFFER:
                          return "Highest FBA offer";

                        case AutoFillListPriceFrom.LOWEST_FBA_OFFER:
                          return "Lowest FBA offer";
                      }
                    }}
                  />
                </Tooltip>
              </span>
              <span>and</span>
              <span>
                <Tooltip title={!user.settings.autoFillListPrice ? 'Enable Auto-fill list price first' : ''}>
                  <Select
                    disabled={!user.settings.autoFillListPrice || loading}
                    value={user.settings?.autoFillListPriceOperation}
                    onChange={e => {
                      user.settings.autoFillListPriceOperation = e;
                    }}
                    options={[AutoFillListPriceOperation.INCREASE,AutoFillListPriceOperation.DECREASE]}
                    optionRender={option => {
                      switch (option) {
                        case AutoFillListPriceOperation.INCREASE:
                          return "Increase";

                        case AutoFillListPriceOperation.DECREASE:
                          return "Decrease";
                      }
                    }}
                  />
                </Tooltip>
              </span>
              <span>by</span>
              <span>
                <Tooltip title={!user.settings.autoFillListPrice ? 'Enable Auto-fill list price first' : ''}>
                  <InputNumber
                    disabled={!user.settings.autoFillListPrice}
                    showControls={false}
                    min={0}
                    max={200}
                    value={autoFillPrice}
                    onChange={e => {
                      setAutoFillPrice(e);
                      user.settings.autoFillListPriceAmount = e.toFixed(2);
                      updateSettings();
                    }}
                    suffix={<span className="weak">%</span>}
                  />
                </Tooltip>
              </span>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </PageContainer>
  );
}
